import {booleanAttribute, Component, Input, OnChanges} from '@angular/core';
import {CampagneProduct} from "../../../models/Campagne/CampagneProduct";
import {DialogModule} from "primeng/dialog";
import {WishlistService} from "../../../services/Wishlists/wishlist.service";
import {ButtonModule} from "primeng/button";
import {DecimalPipe, JsonPipe, NgClass} from "@angular/common";
import {DropdownModule} from "primeng/dropdown";
import {DynamicCurrencyPipe} from "../../../pipes/dynamic-currency.pipe";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {InputNumberModule} from "primeng/inputnumber";
import {CentilisationPipe} from "../../../pipes/centilisation.pipe";
import {ToastMessage} from "../../../models/toastMessage";
import {ToastService} from "../../../services/toast.service";
import {WishlistLigne} from "../../../models/Wishlist/Wishlist";
import {TypeCampagne} from "../../../models/Campagne/TypeCampagne";
import {AutoCompleteCompleteEvent, AutoCompleteModule} from "primeng/autocomplete";
import {firstValueFrom} from "rxjs";
import {TarifService} from "../../../services/tarif.service";
import {InputTextModule} from "primeng/inputtext";
import {ResultatRechercheVin} from "../../../models/Tarif/ResultatRechercheVin";

@Component({
  selector: 'app-add-to-wishlist',
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    DecimalPipe,
    DropdownModule,
    DynamicCurrencyPipe,
    FormsModule,
    InputNumberModule,
    ReactiveFormsModule,
    CentilisationPipe,
    AutoCompleteModule,
    InputTextModule,
    NgClass,
    JsonPipe
  ],
  templateUrl: './add-to-wishlist.component.html',
  styleUrl: './add-to-wishlist.component.scss'
})
export class AddToWishlistComponent implements OnChanges{

  @Input({transform: booleanAttribute}) show: boolean = false;
  @Input() product!: CampagneProduct;

  form!: FormGroup;
  suggestionsVins: ResultatRechercheVin[] = [];

  protected readonly TypeCampagne = TypeCampagne;
  yearPrimeurs: number = new Date().getFullYear() - 1;

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    protected wishlistService: WishlistService,
    private tarifService: TarifService
  ) {
    this.form = this.formBuilder.group({
      vin: [{value: undefined, disabled: false}, Validators.required],
      millesime: [{value: this.getMillesimeByTypeCampain() ?? null , disabled: false}],
      quantity: [{value: undefined, disabled: false}],
    });

  }

  ngOnChanges(){
    if(this.product)
      this.form.controls['vin'].setValue(this.product);
  }

  async rechercherVin($event: AutoCompleteCompleteEvent) {
    if(this.wishlistService.type() === TypeCampagne.Primeurs){
      this.suggestionsVins = await firstValueFrom(this.tarifService.rechercheVinsNonSortis($event.query));
    }
    if(this.wishlistService.type() === TypeCampagne.MiseEnMarche){
      this.suggestionsVins = await firstValueFrom(this.tarifService.rechercheVin($event.query));
    }
  }

  async addToWishlist() {
    //Reset manuel du field vin à valid si le vin est entré après la quantité, dès lors qu'un idVin a été trouvé
    if(this.form.controls['vin'].value.idVin) this.form.controls['vin'].setErrors(null)
    else this.form.controls['vin'].setErrors({vinError : true});

    if(this.form.controls['vin'].status === 'INVALID'){
      this.form.controls['vin'].markAsTouched()
    }

    if(this.form.valid){
      if(this.product){
        await this.saveProductInWishlist()
      }
      else {
        await this.saveVinNonSortiInWishlist()
      }
    }

  }



  createWishlistLigne(productData: CampagneProduct, quantity: number, type: number | null): WishlistLigne {
    return {
      idVin: productData.idVin,
      nomVin: productData.nomVin,
      couleur: productData.couleur,
      millesime: productData.millesime,
      nomAppellation: productData.nomAppellation,
      quantite: quantity,
      typeCampagne: type!,
      pays: productData.pays
    }
  }

  private async saveProductInWishlist() {
    const typeCampagne = this.wishlistService.type();
    const isPrimeurs = typeCampagne === TypeCampagne.Primeurs;
    const wishlist = isPrimeurs ? this.wishlistService.primeursWishlist() : this.wishlistService.memWishlist();
    const listIdVin = isPrimeurs ? this.wishlistService.listIdVinPrimeursWish : this.wishlistService.listIdVinMemWish;

    if (!wishlist?.wishlistLignes?.find(w => w.idVin === this.product.idVin)) {
      const data = this.createWishlistLigne(this.product, this.form.controls['quantity'].value, typeCampagne);
      await this.wishlistService.add(data, wishlist?.idWishlist ?? null);
      listIdVin.set(listIdVin());
      this.wishlistService.hide();

      const message = this.product.nomVin + $localize`:@@bienAjouterWishlist: a bien été ajouté à votre ` +
        (isPrimeurs ? $localize`:@@primeursWishlist: wishlist primeurs.` : $localize`:@@memFullWishlist: wishlist mise en marché.`) + '\n';
      const toastMessage = new ToastMessage('success', message, null, '');
      this.toastService.toastMessage$.next(toastMessage);
    }
  }

  private async saveVinNonSortiInWishlist() {

    const typeCampagne = this.wishlistService.type();
    const isPrimeurs = typeCampagne === TypeCampagne.Primeurs;
    const wishlist = isPrimeurs ? this.wishlistService.primeursWishlist() : this.wishlistService.memWishlist();
    const isVinMillFound = wishlist?.wishlistLignes?.find(w => {
      return (w.idVin === this.form.controls['vin'].value.idVin && w.millesime === this.form.controls['millesime'].value)
    });

    if (!isVinMillFound) {

      const wishlistToCreate = {
        idWishlist: wishlist?.idWishlist,
        typeCampagne: typeCampagne,
        idVin: this.form.controls['vin'].value.idVin,
        millesime: this.form.controls['millesime'].value,
        quantite: this.form.controls['quantity'].value,
      }

      const res = await firstValueFrom(this.wishlistService.createWishlistVinNonSorti(wishlistToCreate));
      this.wishlistService.hide();

      if(wishlist?.idWishlist){
        this.wishlistService.type.set(typeCampagne);
        this.wishlistService.addExistingWishlistLigne(res);
      }
      else{
        window.location.reload();
      }

      const message = res.nomVin + $localize`:@@bienAjouterWishlist: a bien été ajouté à votre ` +
        (isPrimeurs ? $localize`:@@primeursWishlist: wishlist primeurs.` : $localize`:@@memFullWishlist: wishlist mise en marché.`) + '\n';
      const toastMessage = new ToastMessage('success', message, null, '');
      this.toastService.toastMessage$.next(toastMessage);
    }
    else {

      //vin déjà présent en wishlist
      const message = $localize`:@@bienAjouterWishlist: a bien été ajouté à votre ` +
        (isPrimeurs ? $localize`:@@primeursWishlist: wishlist primeurs.` : $localize`:@@memFullWishlist: wishlit mise en marché.`) + '\n';
      const toastMessage = new ToastMessage('success', message, null, '');
      this.toastService.toastMessage$.next(toastMessage);

    }

  }

  isTypeOfVin(value: undefined | string | object) {
    return typeof value === 'object';
  }


  private getMillesimeByTypeCampain() {
    return this.wishlistService.type() !== TypeCampagne.MiseEnMarche ? this.yearPrimeurs.toString() : undefined;
  }

}
